import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Detail, card and form pages link back with a bare path ("/job-orders"), which
 * drops the list's query string — so the user's search, date filters and page
 * number are lost and they have to search all over again.
 *
 * List pages record where they were; Back links read it back.
 *
 * sessionStorage rather than router location.state because it survives a
 * refresh, works however the user reached the detail page (clicked link, typed
 * URL, reopened tab), and can be read from a post-save navigate() too — none of
 * which hold for state passed through a Link. Scoped to the tab and cleared
 * with it, so one tab's filters never leak into another's.
 */
const KEY = 'kpjm.listReturn';

function read(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/** Call from a list page: remembers its current URL (path + query). */
export function useRememberListUrl(): void {
  const { pathname, search } = useLocation();
  useEffect(() => {
    try {
      const map = read();
      map[pathname] = pathname + search;
      sessionStorage.setItem(KEY, JSON.stringify(map));
    } catch {
      // Storage unavailable (private mode / quota). Back then just falls back
      // to the bare list path, which is the old behaviour — never an error.
    }
  }, [pathname, search]);
}

/** Where Back should go: the remembered list URL, else the bare path. */
export function listReturnUrl(base: string): string {
  return read()[base] ?? base;
}
